/* ------------------------------------------------------------------ *
 * reportData — pure, framework-free helpers for the Report Detail view *
 * Extracted from ReportDetailPage so the logic is unit-testable and    *
 * shared by the (future) PDF/CSV parity checks.                        *
 * ------------------------------------------------------------------ */

/**
 * Build the Prescription ("권장 대안" / safe-alternative) rows from rule_hits.
 *
 * A row is kept ONLY when the hit carries a term-specific `suggested_alternative`.
 * The previous code fell back to a generic per-report `recommendation`, which
 * surfaced phrases unrelated to the flagged term ("irrelevant phrases" bug).
 * Dropping that fallback — and requiring a real, non-echo alternative — keeps the
 * SAFE ALTERNATIVE column relevant to its flagged term. The backend rule engine
 * now attaches `suggested_alternative` + `rationale` per matched term (Path A),
 * so real reports keep populated rows.
 *
 * Show-all (Report-show-all-flagged-recommendation-8-23 Phase 1): the former
 * `.slice(0, 3)` cap is gone — EVERY flagged term with a real, non-echo
 * alternative is surfaced. Inputs are short (≤1 CU / ~180 words), so the row
 * count tracks the actual number of distinct violations.
 *
 * @param {Array<object>} ruleHits
 * @returns {Array<{flagged: string, safe: string, why: string}>} one row per relevant hit
 */
function _toRow(h) {
  return {
    flagged: (h.matched_terms && h.matched_terms.length)
      ? h.matched_terms.join(', ')
      : (h.rule_name || ''),
    safe: h.suggested_alternative || '',            // no generic h.recommendation fallback
    why: h.rationale || h.why || h.explanation || '',
  };
}

// A row is kept ONLY when it carries a real, non-echo alternative for its term.
function _isRelevant(r) {
  return !!(r.flagged
    && r.safe
    && r.safe.trim().toLowerCase() !== r.flagged.trim().toLowerCase());
}

export function buildPrescriptionRows(ruleHits) {
  return (ruleHits || []).map(_toRow).filter(_isRelevant);
}

/**
 * Group the Prescription into ONE CARD PER FLAGGED SENTENCE
 * (Report-show-all-flagged-recommendation-8-23 Phase 2).
 *
 * Each rule_hit carries `source_sentences` (the sentence(s) of the input it
 * occurs in, stamped by the backend rule engine). We emit one group per distinct
 * sentence, in first-appearance order, listing every flagged phrase in that
 * sentence with its compliant alternative + why. A hit whose term appears in
 * several sentences shows under each. Hits WITHOUT `source_sentences` (legacy
 * reports predating Phase 2, or a term that located no sentence) fall into a
 * trailing group with `sentence: null` — rendered as the flat Phase-1 list, so
 * nothing is ever dropped.
 *
 * @returns {Array<{sentence: string|null, flags: Array<{flagged,safe,why}>}>}
 */
export function buildPrescriptionGroups(ruleHits) {
  const groups = [];
  const bySentence = new Map();
  const orphans = [];

  (ruleHits || []).forEach((h) => {
    const row = _toRow(h);
    if (!_isRelevant(row)) return;
    const sentences = Array.isArray(h.source_sentences)
      ? h.source_sentences.filter((s) => s && String(s).trim())
      : [];
    if (sentences.length === 0) { orphans.push(row); return; }
    sentences.forEach((s) => {
      let g = bySentence.get(s);
      if (!g) { g = { sentence: s, flags: [] }; bySentence.set(s, g); groups.push(g); }
      // de-dupe identical flag rows within one sentence
      if (!g.flags.some((f) => f.flagged === row.flagged && f.safe === row.safe)) {
        g.flags.push(row);
      }
    });
  });

  if (orphans.length) groups.push({ sentence: null, flags: orphans });
  return groups;
}
